/* eslint-disable @typescript-eslint/no-unused-vars */
import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { PlaceModel } from '@app/core/models';
import { SnackbarService, StorageService } from '@app/core/services';
import { PlaceApiService } from '@app/shared/services/api';

const TSTATE_KEY = makeStateKey<PlaceModel>('place-resolver');

@Injectable()
export class PlaceResolver implements Resolve<PlaceModel> {
  private readonly isServer: boolean;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: any,
    private readonly placeApiService: PlaceApiService,
    private readonly transferState: TransferState,
    private readonly storageService: StorageService,
    private readonly router: Router,
    private readonly snackbarService: SnackbarService,
  ) {
    this.isServer = isPlatformServer(this.platformId);
  }

  resolve(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<PlaceModel> | Promise<PlaceModel> | PlaceModel | null {
    const slug = route.paramMap.get('placeSlug');
    // console.log(slug);
    return this.isServer ? this.serverResolve(slug) : this.browserResolve(slug);
  }

  private browserResolve(slug: string): Observable<PlaceModel> | PlaceModel {
    const tmp = this.transferState.get<Pick<PlaceModel, keyof PlaceModel>>(
      TSTATE_KEY,
      null,
    );

    if (tmp != null) {
      this.transferState.remove<PlaceModel>(TSTATE_KEY);
      return PlaceModel.copyObj(tmp);
    }

    return this.placeApiService.getPlace({ slug }).pipe(
      catchError(err => {
        this.snackbarService.showError('Page not found', 1000);
        this.router.navigate(['page-not-found']);
        console.log(err);
        return of(null);
      }),
    );
  }

  private serverResolve(slug: string): Observable<PlaceModel> {
    return this.placeApiService.getPlace({ slug }).pipe(
      tap(data => {
        this.transferState.set<PlaceModel>(TSTATE_KEY, data);
      }),
      catchError(err => {
        this.router.navigate(['page-not-found']);
        console.log(err);
        this.transferState.set<PlaceModel>(TSTATE_KEY, null);
        return of(null);
      }),
    );
  }
}
